import { useMemo, useState } from 'react';
import { MoreHorizontalIcon, SearchIcon, SearchXIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ChipRow } from '@/shared/components/ChipRow';
import { ActionSheet } from '@/components/ui/phone/ActionSheet';
import { PhoneCard, PhoneCardList } from '@/components/ui/phone/PhoneCardList';
import { forgeName } from '@/features/identity';
import type {
  ForgeHostColumn,
  PersonLinksView,
  PersonLinkView,
} from '@/lib/api/generated/model';
import { ConfirmDestructiveDialog } from '@/shared/components/ConfirmDestructiveDialog';
import { EmptyState } from '@/shared/components/EmptyState';
import { EyebrowLabel } from '@/shared/components/EyebrowLabel';
import { RetryButton } from '@/shared/components/RetryButton';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';

import { usePeopleDirectory, useRevokeLink } from '../hooks/useLinkedAccounts';
import {
  filterCounts,
  hostsInUse,
  linkCell,
  matchesFilter,
  matchesSearch,
  PEOPLE_FILTERS,
  sortPeople,
  type PeopleFilter,
} from '../linkedAccounts';
import {
  COMMIT_IDENTITY_COPY as WORDS,
  LINKED_ACCOUNTS_COPY as COPY,
} from '../linkedAccountsCopy';
import { LinkChip } from './LinkChip';

interface Removal {
  userId: string;
  person: string;
  link: PersonLinkView;
}

/**
 * Everyone and their linked accounts, built to scan (spec 162): one chip per
 * host, the stuck first, a slice at a time. The whole organization is read so
 * the counts are true for everyone (R1); the hosts no workspace uses stay out
 * of the way, and out of "Not linked", unless asked for (US3).
 */
export function PeopleDirectory({
  q,
  show,
  onChange,
}: {
  q: string;
  show: PeopleFilter;
  onChange: (next: { q?: string; show?: PeopleFilter }) => void;
}) {
  const directory = usePeopleDirectory();
  const revoke = useRevokeLink();
  const [removal, setRemoval] = useState<Removal | null>(null);
  const [showUnused, setShowUnused] = useState(false);
  // One "now" per visit: a row must not change its standing while it is read.
  const [now] = useState(() => new Date());
  const showLoading = usePendingDisplay(directory.isPending);
  const isPhone = usePhoneWidth();

  const data = directory.data;
  const inUse = useMemo(() => hostsInUse(data?.hosts ?? []), [data?.hosts]);
  const counts = useMemo(
    () => filterCounts(data?.items ?? [], inUse, now),
    [data?.items, inUse, now],
  );
  const rows = useMemo(
    () =>
      sortPeople(
        (data?.items ?? []).filter(
          (row) =>
            matchesFilter(row, show, inUse, now) && matchesSearch(row, q),
        ),
        inUse,
        now,
      ),
    [data?.items, show, q, inUse, now],
  );
  const unused = (data?.hosts ?? []).filter((host) => host.unused);
  const columns = (data?.hosts ?? []).filter(
    (host) => !host.unused || showUnused,
  );

  return (
    <section
      aria-labelledby="commit-identity-people"
      className="flex min-w-0 flex-col gap-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="commit-identity-people">
          <EyebrowLabel>{WORDS.people}</EyebrowLabel>
        </h2>
        {data ? (
          <span className="text-muted-foreground text-xs tabular-nums">
            {WORDS.shown(rows.length, data.items.length)}
          </span>
        ) : null}
      </div>

      <PeopleToolbar q={q} show={show} counts={counts} onChange={onChange} />

      {directory.isPending ? (
        showLoading ? (
          <p className="text-muted-foreground text-sm">{WORDS.loading}</p>
        ) : null
      ) : directory.isError || data === undefined ? (
        <div className="flex items-center gap-3">
          <p className="text-muted-foreground text-sm">{COPY.peopleFailed}</p>
          <RetryButton
            onRetry={() => void directory.refetch()}
            isFetching={directory.isFetching}
          />
        </div>
      ) : data.items.length === 0 ? (
        <p className="text-muted-foreground text-sm">{COPY.noPeople}</p>
      ) : (
        <div className="border-border bg-card overflow-hidden rounded-md border">
          {inUse.length === 0 ? (
            <p className="text-muted-foreground border-border border-b px-3 py-2 text-sm">
              {WORDS.noHosts}
            </p>
          ) : null}
          {rows.length === 0 ? (
            <EmptyState
              icon={SearchXIcon}
              description={WORDS.nobodyMatches}
              action={
                <Button
                  variant="outline"
                  onClick={() => onChange({ q: undefined, show: undefined })}
                >
                  {WORDS.showEveryone}
                </Button>
              }
            />
          ) : isPhone ? (
            <PhoneCardList label={WORDS.people} className="p-2">
              {rows.map((row) => (
                <PersonCard
                  key={row.person.id}
                  row={row}
                  columns={columns}
                  now={now}
                  onRemove={(link) =>
                    setRemoval({
                      userId: row.person.id,
                      person: row.person.display_name,
                      link,
                    })
                  }
                />
              ))}
            </PhoneCardList>
          ) : (
            <div className="max-w-full overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-border border-b text-left">
                    <th
                      scope="col"
                      className="text-muted-foreground px-3 py-2 text-xs font-medium"
                    >
                      {COPY.person}
                    </th>
                    {columns.map((column) => (
                      <HostHeader key={column.host} column={column} />
                    ))}
                    <th scope="col" className="w-12 px-2 py-2">
                      <span className="sr-only">{WORDS.actions}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-border divide-y">
                  {rows.map((row) => (
                    <PersonRow
                      key={row.person.id}
                      row={row}
                      columns={columns}
                      now={now}
                      onRemove={(link) =>
                        setRemoval({
                          userId: row.person.id,
                          person: row.person.display_name,
                          link,
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {unused.length > 0 ? (
            <div className="border-border border-t px-3 py-1">
              <Button
                variant="link"
                size="sm"
                className="h-auto px-0"
                aria-pressed={showUnused}
                onClick={() => setShowUnused((value) => !value)}
              >
                {showUnused
                  ? WORDS.hideUnused
                  : WORDS.showUnused(unused.length)}
              </Button>
            </div>
          ) : null}
        </div>
      )}

      <ConfirmDestructiveDialog
        open={removal !== null}
        title={
          removal
            ? COPY.removeTitle(removal.person, forgeName(removal.link.provider))
            : ''
        }
        confirmLabel={COPY.remove}
        pendingLabel={COPY.removePending}
        isPending={revoke.isPending}
        error={revoke.error ? COPY.removeFailed : null}
        onConfirm={() => {
          if (!removal) return;
          revoke.mutate(
            {
              userId: removal.userId,
              host: removal.link.host,
              person: removal.person,
              provider: removal.link.provider,
            },
            { onSuccess: () => setRemoval(null) },
          );
        }}
        onCancel={() => {
          revoke.reset();
          setRemoval(null);
        }}
      >
        {removal ? COPY.removeBody(removal.link.host) : null}
      </ConfirmDestructiveDialog>
    </section>
  );
}

/**
 * The search and the slices, as the Limits page has them (spec 117): each chip
 * says how many people it keeps, so a reader knows before pressing it. The
 * search keeps its own draft: the address is where the value lives, but a
 * field that re-renders from the router after each keystroke loses characters.
 */
function PeopleToolbar({
  q,
  show,
  counts,
  onChange,
}: {
  q: string;
  show: PeopleFilter;
  counts: Record<PeopleFilter, number>;
  onChange: (next: { q?: string; show?: PeopleFilter }) => void;
}) {
  const [draft, setDraft] = useState(q);
  const [lastQuery, setLastQuery] = useState(q);
  // An outside change (Show everyone, the back button) wins over the draft.
  if (q !== lastQuery) {
    setLastQuery(q);
    setDraft(q);
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="relative min-w-48 flex-1">
        <span className="sr-only">{COPY.search}</span>
        <SearchIcon
          aria-hidden="true"
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
        />
        <Input
          type="search"
          value={draft}
          placeholder={COPY.search}
          onChange={(event) => {
            setDraft(event.target.value);
            setLastQuery(event.target.value);
            onChange({ q: event.target.value || undefined });
          }}
          className="ps-8"
        />
      </label>
      <ChipRow label={WORDS.filtersLabel}>
        {PEOPLE_FILTERS.map((option) => {
          const pressed = option === show;
          // Pressed reads in the foreground on the primary outline, not Light Blue:
          // small text in that colour fails on the dark theme (WD-04).
          return (
            <button
              key={option}
              type="button"
              aria-pressed={pressed}
              onClick={() =>
                onChange({ show: option === 'all' ? undefined : option })
              }
              className={`focus-visible:ring-ring touch-target inline-flex min-h-9 pointer-coarse:min-h-11 items-center gap-1.5 rounded-full border px-3 py-1 text-sm whitespace-nowrap focus-visible:ring-3 focus-visible:outline-none ${
                pressed
                  ? 'border-primary/50 bg-primary/10 text-foreground'
                  : 'border-border bg-card text-foreground hover:bg-accent/50'
              }`}
            >
              {WORDS.filters[option]}{' '}
              <span className="text-muted-foreground tabular-nums">
                {counts[option]}
              </span>
            </button>
          );
        })}
      </ChipRow>
    </div>
  );
}

function HostHeader({ column }: { column: ForgeHostColumn }) {
  return (
    <th scope="col" className="px-3 py-2 font-medium">
      <span className="text-foreground block text-xs">{column.host}</span>
      <span className="text-muted-foreground block text-xs font-normal">
        {forgeName(column.provider)}
        {column.unused ? ` · ${WORDS.unusedColumn}` : ''}
      </span>
    </th>
  );
}

function PersonRow({
  row,
  columns,
  now,
  onRemove,
}: {
  row: PersonLinksView;
  columns: readonly ForgeHostColumn[];
  now: Date;
  onRemove: (link: PersonLinkView) => void;
}) {
  // Every link that can still be removed, on any host, shown or not.
  const removable = row.links.filter((link) => link.status !== 'revoked');
  return (
    <tr className="hover:bg-muted/50 transition-colors">
      <th scope="row" className="px-3 py-2 text-left font-normal">
        <span className="text-foreground block">{row.person.display_name}</span>
        <span className="text-muted-foreground block text-xs">
          {row.person.email}
        </span>
      </th>
      {columns.map((column) => (
        <td key={column.host} className="px-3 py-2">
          <LinkChip link={linkCell(row, column.host)} now={now} />
        </td>
      ))}
      <td className="px-2 py-2 text-right">
        {removable.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button variant="ghost" size="icon" className="shrink-0">
                  <MoreHorizontalIcon aria-hidden="true" />
                  <span className="sr-only">
                    {WORDS.rowMenu(row.person.display_name)}
                  </span>
                </Button>
              }
            />
            <DropdownMenuContent align="end">
              {removable.map((link) => (
                <DropdownMenuItem
                  key={link.host}
                  variant="destructive"
                  onClick={() => onRemove(link)}
                >
                  {WORDS.removeItem(forgeName(link.provider), link.host)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * A person as a card on a phone (spec 263): their name and email, then one
 * line per host with its chip, where the desktop has a column per host.
 */
function PersonCard({
  row,
  columns,
  now,
  onRemove,
}: {
  row: PersonLinksView;
  columns: readonly ForgeHostColumn[];
  now: Date;
  onRemove: (link: PersonLinkView) => void;
}) {
  const removable = row.links.filter((link) => link.status !== 'revoked');
  return (
    <PhoneCard
      title={row.person.display_name}
      actions={
        <ActionSheet
          name={row.person.display_name}
          actions={removable.map((link) => ({
            label: WORDS.removeItem(forgeName(link.provider), link.host),
            destructive: true,
            onSelect: () => {
              onRemove(link);
            },
          }))}
        />
      }
      lines={[
        row.person.email,
        ...columns.map((column) => (
          <span
            key={column.host}
            className="flex flex-wrap items-center justify-between gap-2"
          >
            <span className="[overflow-wrap:anywhere]">
              {column.host}
              {column.unused ? ` · ${WORDS.unusedColumn}` : ''}
            </span>
            <LinkChip link={linkCell(row, column.host)} now={now} />
          </span>
        )),
      ]}
    />
  );
}
